import * as React from 'react';
import { Suspense } from 'react';
import { Outlet, type RouteObject, ScrollRestoration } from 'react-router-dom';

import { appConfig } from '../../appConfig';
import { appRoutes } from '../../routes';
import { MicroAppContextProvider, MicroAppContextType } from '../common/contexts/microAppContext';
import { ReactAppWrapperWithoutRouter } from '../common/init/ReactAppWrapper';
import {
    getMicroAppInitialQueryData,
    setMicroAppInitialQueryData,
} from '../common/init/renderMicroApp.utils';
import { trimTrailingSlash } from '../common/utils/stringUtils';
import EpiPageLoader from '../episerver/components/base/EpiPageLoader';
import EcommerceLayout, { EcommercePageLayout } from './components/EcommerceLayout';
import EcommercePage from './components/pages/ecommercePage/EcommercePage';
import { ProductSectionsContextProvider } from './contexts/productSectionsContext';
import { RouterContextProvider } from './contexts/useRouterContext';
import { useContentPageSizeSync } from './hooks/useContentPageSizeSync';
import { useCurrentProductOrSection } from './hooks/useCurrentProductOrSection';
import { useEcommerceQueryStringTriggers } from './hooks/useEcommerceQueryStringTriggers';

const EcommerceQueryStringTriggersSetup = () => {
    useEcommerceQueryStringTriggers();
    return null;
};

const LazyProductSectionPage = React.lazy(
    () => import('./components/pages/productSectionPage/ProductSectionPage'),
);

const LazyProductDetailsPage = React.lazy(
    () => import('./components/pages/productDetailsPage/ProductDetailsPage'),
);

const SuspendableProductOrSectionPage = () => {
    const {
        routeData: { productId },
    } = useCurrentProductOrSection();

    if (productId) {
        return (
            <Suspense fallback={null}>
                <LazyProductDetailsPage />
            </Suspense>
        );
    }

    return (
        <Suspense fallback={null}>
            <LazyProductSectionPage />
        </Suspense>
    );
};

const EcommerceEpiPageLoader = () => {
    const { handlePageLoad } = useContentPageSizeSync();

    return <EpiPageLoader onPageLoad={handlePageLoad} />;
};

const checkoutRoutes: RouteObject[] = [
    {
        loader: () => {
            const initialQueryData = getMicroAppInitialQueryData();
            setMicroAppInitialQueryData(initialQueryData);
            return null;
        },
        path: trimTrailingSlash(appRoutes.ecommerce.checkout.index.url),
        element: (
            <MicroAppContextProvider microAppType={MicroAppContextType.Checkout}>
                <ReactAppWrapperWithoutRouter manageRouteFocus={false}>
                    <ScrollRestoration />
                    <Outlet />
                </ReactAppWrapperWithoutRouter>
            </MicroAppContextProvider>
        ),
        children: [
            {
                path: trimTrailingSlash(appRoutes.ecommerce.checkout.orderComplete.url),
                element: (
                    <EcommerceLayout>
                        <Outlet />
                    </EcommerceLayout>
                ),
                children: [
                    {
                        index: true,
                        lazy: async () => import('./routes/OrderConfirmationRoute'),
                    },
                ],
            },
        ],
    },
];

const ecommerceRoutes: RouteObject[] = [
    {
        loader: () => {
            const initialQueryData = getMicroAppInitialQueryData();
            setMicroAppInitialQueryData(initialQueryData);
            return null;
        },
        path: appRoutes.ecommerce.index.url,
        element: (
            <MicroAppContextProvider microAppType={MicroAppContextType.Ecommerce}>
                <ReactAppWrapperWithoutRouter manageRouteFocus>
                    <RouterContextProvider>
                        <EcommerceLayout>
                            <ScrollRestoration />
                            <EcommerceQueryStringTriggersSetup />
                            <ProductSectionsContextProvider>
                                <EcommercePageLayout
                                    ecommercePageContentId={
                                        appConfig.coopSettings.ecommerce.page.contentId
                                    }
                                >
                                    <Outlet />
                                </EcommercePageLayout>
                            </ProductSectionsContextProvider>
                        </EcommerceLayout>
                    </RouterContextProvider>
                </ReactAppWrapperWithoutRouter>
            </MicroAppContextProvider>
        ),
        children: [
            {
                index: true,
                element: <EcommercePage />,
            },
            {
                path: 'varor',
                children: [
                    {
                        path: '*',
                        element: <SuspendableProductOrSectionPage />,
                    },
                ],
            },
            {
                path: 'punchout',
                lazy: async () => import('./routes/PunchoutLoginRoute'),
            },
            {
                path: '*',
                element: <EcommerceEpiPageLoader />,
            },
        ],
    },
    ...checkoutRoutes,
];

export default ecommerceRoutes;
